import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  
  // Determinar si estamos en la página de inicio
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      // Consideramos que ha hecho scroll si está más allá de 50px
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    
    // Verificar posición inicial al cargar
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navigation = [
    { name: 'Inicio', href: '/' },
    { name: 'Servicios', href: '/services' },
    { name: 'Industrias', href: '/industries' },
    { name: 'Productos', href: '/products' },
    { name: 'Nosotros', href: '/about' },
    { name: 'Contacto', href: '/contact' },
  ];

  const handleQuoteRequest = () => {
    // Navigate to quote page
    window.location.href = '/quote';
    // Close mobile menu if open
    setIsMenuOpen(false);
  };


  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 header-transition ${
        isScrolled || isMenuOpen
          ? 'py-4 px-6 mx-auto max-w-6xl mt-4 rounded-2xl header-glass header-scrolled'
          : 'w-full bg-transparent'
      }`}
    >
      <div className={`${(isScrolled || isMenuOpen) ? '' : 'container-custom'} ${isMenuOpen ? 'pb-4' : ''}`}>
        <div className={`flex items-center justify-between ${isScrolled ? 'h-14' : 'h-16'}`}>
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img
                src={
                  isHomePage
                    ? ((isScrolled || isMenuOpen) ? "/logo.png" : "/logo-blanco.png")
                    : "/logo.png"
                }
                alt="Atomic Corporation"
                className={`h-12 w-auto transition-all duration-300 ${
                  (isScrolled || isMenuOpen) ? 'opacity-100' : 'opacity-95'
                }`}
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`transition-colors font-medium ${
                  (isScrolled || isMenuOpen)
                    ? 'text-gray-700 hover:text-primary-600'
                    : (isHomePage
                        ? 'text-white hover:text-gray-200 header-transparent-text'
                        : 'text-gray-700 hover:text-primary-600')
                } ${
                  location.pathname === item.href
                    ? ((isScrolled || isMenuOpen)
                        ? 'text-primary-600'
                        : (isHomePage ? 'text-white font-semibold' : 'text-primary-600 font-semibold'))
                    : ''
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Contact Info */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              to="/admin"
              className={`p-2 rounded-md transition-colors ${
                (isScrolled || isMenuOpen)
                  ? 'text-gray-600 hover:text-primary-600 hover:bg-gray-100'
                  : (isHomePage
                      ? 'text-white hover:text-gray-200 hover:bg-white/10 header-transparent-text'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100')
              }`}
              title="Panel de Administración"
            >
              <Settings className="w-5 h-5" />
            </Link>
            <div className={`flex items-center space-x-2 text-sm ${
              (isScrolled || isMenuOpen)
                ? 'text-gray-600'
                : (isHomePage ? 'text-white header-transparent-text' : 'text-gray-600')
            }`}>
              <Phone className="w-4 h-4" />
              <span>+51 953 759 419</span>
            </div>
            <Button
              className={`${
                (isScrolled || isMenuOpen)
                  ? 'btn-primary'
                  : (isHomePage
                      ? 'bg-white text-primary-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200'
                      : 'btn-primary')
              }`}
              onClick={handleQuoteRequest}
            >
              Cotizar Ahora
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-md transition-colors ${
                (isScrolled || isMenuOpen)
                  ? 'text-gray-700 hover:text-primary-600 hover:bg-gray-100'
                  : (isHomePage
                      ? 'text-white hover:text-gray-200 hover:bg-white/10 header-transparent-text'
                      : 'text-gray-700 hover:text-primary-600 hover:bg-gray-100')
              }`}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - Directly in the main container */}
        {isMenuOpen && (
          <>
            <div className={`md:hidden border-t pt-4 mt-4 ${
              isScrolled || isMenuOpen
                ? ''
                : (isHomePage
                    ? 'bg-black/20 backdrop-blur-sm'
                    : 'bg-white/95 backdrop-blur-sm')
            }`}>
              <nav className="flex flex-col space-y-3">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMenuOpen(false)}
                    className={`transition-colors font-medium px-2 py-2 text-left ${
                      (isScrolled || isMenuOpen)
                        ? 'text-gray-700 hover:text-primary-600'
                        : (isHomePage
                            ? 'text-white hover:text-gray-200 header-transparent-text'
                            : 'text-gray-700 hover:text-primary-600')
                    } ${
                      location.pathname === item.href
                        ? ((isScrolled || isMenuOpen)
                            ? 'text-primary-600'
                            : (isHomePage ? 'text-white font-semibold' : 'text-primary-600 font-semibold'))
                        : ''
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <div className={`pt-4 border-t ${
                  (isScrolled || isMenuOpen)
                    ? 'border-gray-200'
                    : (isHomePage ? 'border-white/20' : 'border-gray-200')
                }`}>
                  <div className="flex flex-col space-y-3">
                    <div className={`flex items-center space-x-2 text-sm px-2 ${
                      (isScrolled || isMenuOpen)
                        ? 'text-gray-600'
                        : (isHomePage ? 'text-white header-transparent-text' : 'text-gray-600')
                    }`}>
                      <Phone className="w-4 h-4" />
                      <span>+51 953 759 419</span>
                    </div>
                    <div className={`flex items-center space-x-2 text-sm px-2 ${
                      (isScrolled || isMenuOpen)
                        ? 'text-gray-600'
                        : (isHomePage ? 'text-white header-transparent-text' : 'text-gray-600')
                    }`}>
                      <Mail className="w-4 h-4" />
                      <span>ventas@atomcorporations.com</span>
                    </div>
                    <Link
                      to="/admin"
                      className={`flex items-center justify-center space-x-2 text-sm px-4 py-2 rounded-md transition-colors mx-auto max-w-xs ${
                        (isScrolled || isMenuOpen)
                          ? 'text-gray-600 hover:text-primary-600 hover:bg-gray-100'
                          : (isHomePage
                              ? 'text-white hover:text-gray-200 hover:bg-white/10 header-transparent-text'
                              : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100')
                      }`}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Panel Admin</span>
                    </Link>
                    <Button
                      className={`${
                        (isScrolled || isMenuOpen)
                          ? 'btn-primary mx-auto max-w-xs'
                          : (isHomePage
                              ? 'bg-white text-primary-600 hover:bg-gray-100 mx-auto max-w-xs px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200'
                              : 'btn-primary mx-auto max-w-xs')
                      }`}
                      onClick={handleQuoteRequest}
                    >
                      Cotizar Ahora
                    </Button>
                  </div>
                </div>
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
